<script setup lang="ts">
  /**
   * Application header — 03-components/05a-application-header · OSCloud DS 1.1.124
   */
  import { computed, onBeforeUnmount, onMounted, ref, useId } from 'vue'
  import type { RouteLocationRaw } from 'vue-router'

  import BfApplicationMenu from './BfApplicationMenu.vue'
  import BfIconBell from '@/components/ui/icons/BfIconBell.vue'
  import BfIconChevronDown from '@/components/ui/icons/BfIconChevronDown.vue'
  import BfIconMenu from '@/components/ui/icons/BfIconMenu.vue'
  import type { BfApplicationHeaderNavItem } from '@/typescript/types/ui-components/BfApplicationHeaderI'
  import type {
    BfApplicationMenuGroup,
    BfApplicationMenuItem,
    BfApplicationMenuNotification,
    BfApplicationMenuUser,
  } from '@/typescript/types/ui-components/BfApplicationMenuI'

  type Disclosure = 'notifications' | 'account'

  const props = withDefaults(
    defineProps<{
      user?: BfApplicationMenuUser | null
      accountGroups?: BfApplicationMenuGroup[]
      notifications?: BfApplicationMenuNotification[]
      notificationsLabel?: string
      logoTo?: RouteLocationRaw | null
      logoHref?: string
      logoLabel?: string
      /** `id` of the BfConsoleNav the hamburger opens. */
      navigationId?: string
      /** Drawer state, bound with v-model:navigation-open. */
      navigationOpen?: boolean
      /** Optional product navigation between the brand and the actions. Hidden below --bf-bp-console. */
      navItems?: BfApplicationHeaderNavItem[]
      navLabel?: string
      /** The --deep-blue bar, for public product pages. */
      dark?: boolean
    }>(),
    {
      user: null,
      accountGroups: () => [],
      notifications: () => [],
      notificationsLabel: 'Notifications',
      logoTo: null,
      logoHref: '#',
      logoLabel: 'OS Cloud — start',
      navigationId: 'console-navigation',
      navigationOpen: false,
      navItems: () => [],
      navLabel: 'Products',
      dark: false,
    },
  )

  const emit = defineEmits<{
    'update:navigationOpen': [open: boolean]
    select: [item: BfApplicationMenuItem]
    /** A `toggle` row in the account menu changed. */
    toggle: [item: BfApplicationMenuItem, value: boolean]
  }>()

  const notificationsMenuId = useId()
  const accountMenuId = useId()

  const root = ref<HTMLElement | null>(null)
  const openDisclosure = ref<Disclosure | null>(null)

  const unreadCount = computed(() => props.notifications.filter(notification => notification.unread).length)

  const notificationsLabelText = computed(() =>
    unreadCount.value ? `${props.notificationsLabel}, ${unreadCount.value} unread` : props.notificationsLabel,
  )

  /* Required, not optional: below --bf-bp-sm the name is hidden and the avatar takes its place, so
     the text node the trigger took its accessible name from is gone (DS 1.1.124). */
  const accountLabelText = computed(() => (props.user ? `Account menu, ${props.user.name}` : 'Account menu'))

  const isOpen = (disclosure: Disclosure): boolean => openDisclosure.value === disclosure

  // A menu, not a dialog: one open at a time, no focus trap and no scrim.
  const toggle = (disclosure: Disclosure): void => {
    openDisclosure.value = isOpen(disclosure) ? null : disclosure
  }

  const dismiss = (): void => {
    openDisclosure.value = null
  }

  const onDocumentPointerDown = (event: MouseEvent): void => {
    if (root.value && !root.value.contains(event.target as Node)) dismiss()
  }

  const onDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') dismiss()
  }

  onMounted(() => {
    document.addEventListener('pointerdown', onDocumentPointerDown)
    document.addEventListener('keydown', onDocumentKeydown)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', onDocumentPointerDown)
    document.removeEventListener('keydown', onDocumentKeydown)
  })

  const onSelect = (item: BfApplicationMenuItem): void => {
    dismiss()
    emit('select', item)
  }

  /**
   * A row that navigates closes the menu behind it. The row is a real link, so nothing else here
   * runs — without this the menu would stay open over the page it just opened, and a row leading
   * to the current route would leave it open with no navigation to notice at all.
   */
  const onNavigate = (): void => {
    dismiss()
  }
</script>

<template>
  <header ref="root" class="application-header" :class="{ 'application-header--dark': dark }">
    <div class="application-header__brand">
      <button
        type="button"
        class="application-header__menu-button"
        aria-label="Open navigation"
        :aria-expanded="navigationOpen"
        :aria-controls="navigationId"
        @click="emit('update:navigationOpen', !navigationOpen)"
      >
        <BfIconMenu class="application-header__icon" :size="20" />
      </button>

      <component
        :is="logoTo ? 'RouterLink' : 'a'"
        class="application-header__logo-link"
        :to="logoTo"
        :href="logoTo ? undefined : logoHref"
        :aria-label="logoLabel"
      >
        <slot name="logo" />
      </component>
    </div>

    <nav v-if="navItems.length" class="application-header__navigation" :aria-label="navLabel">
      <component
        :is="item.to ? 'RouterLink' : 'a'"
        v-for="item in navItems"
        :key="item.label"
        class="application-header__navigation-link"
        :to="item.to"
        :href="item.to ? undefined : (item.href ?? '#')"
        :aria-current="item.current ? 'page' : undefined"
      >
        {{ item.label }}
      </component>
    </nav>

    <div v-if="$slots.center" class="application-header__center">
      <slot name="center" />
    </div>

    <div class="application-header__actions">
      <slot name="actions" />
      <div v-if="notifications.length" class="application-header__disclosure">
        <button
          type="button"
          class="application-header__trigger"
          :aria-label="notificationsLabelText"
          :aria-expanded="isOpen('notifications')"
          :aria-controls="notificationsMenuId"
          @click="toggle('notifications')"
        >
          <BfIconBell class="application-header__icon" :size="20" />
          <span v-if="unreadCount" class="application-header__badge" aria-hidden="true">{{ unreadCount }}</span>
        </button>

        <BfApplicationMenu
          :id="notificationsMenuId"
          :open="isOpen('notifications')"
          :notifications="notifications"
          :notifications-label="notificationsLabel"
          @navigate="onNavigate"
        />
      </div>

      <div v-if="user" class="application-header__disclosure">
        <button
          type="button"
          class="application-header__trigger"
          :aria-label="accountLabelText"
          :aria-expanded="isOpen('account')"
          :aria-controls="accountMenuId"
          @click="toggle('account')"
        >
          <span v-if="user.initials" class="application-header__avatar" aria-hidden="true">{{ user.initials }}</span>
          <span class="application-header__user-name">{{ user.name }}</span>
          <BfIconChevronDown class="application-header__caret" :size="16" />
        </button>

        <BfApplicationMenu
          :id="accountMenuId"
          :open="isOpen('account')"
          :user="user"
          :groups="accountGroups"
          @select="onSelect"
          @navigate="onNavigate"
          @toggle="(item, value) => emit('toggle', item, value)"
        />
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
  .application-header {
    --application-header-padding-horizontal: 24px; /* the card's declared exception to --bf-sp-* */
    --application-header-action-gap: 16px; /* the card's declared exception to --bf-sp-* */
    --application-header-height-narrow: 60px; /* the bar below --bf-bp-sm; 44px trigger + 8px above and below */
    position: sticky;
    top: 0;
    z-index: 30;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--application-header-action-gap);
    height: var(--bf-header-h-app);
    padding: 0 var(--application-header-padding-horizontal);
    background: var(--bf-white);
    border-bottom: 1px solid var(--bf-line-2);
    font-family: var(--bf-font-body);
    color: var(--bf-fg-1);

    &__brand {
      display: flex;
      align-items: center;
      gap: var(--bf-sp-2);
      min-width: 0;
    }

    &__logo-link {
      display: inline-flex;
      align-items: center;
      border-radius: var(--bf-r-sm);

      &:focus-visible {
        outline: 2px solid var(--bf-orange-dark);
        outline-offset: 2px;
      }

      /* The card sizes `.application-header__logo` — a class the consumer puts on the logo itself.
         A bare <svg> is matched too, so a slot that passes an unclassed icon still gets the height;
         an <img> logo (what the app passes) needs the class. */
      :deep(.application-header__logo),
      :deep(svg) {
        display: block;
        height: 40px;
        width: auto;
      }
    }

    /* Optional product navigation. The current page is aria-current, not a class. */
    &__navigation {
      --application-header-navigation-gap: 32px; /* the card's declared exception to --bf-sp-* */
      display: flex;
      align-items: center;
      align-self: stretch;
      gap: var(--application-header-navigation-gap);
      flex: 1 1 auto;
      min-width: 0;
      margin-left: var(--bf-sp-4);
    }

    &__navigation-link {
      display: inline-flex;
      align-items: center;
      align-self: stretch;
      padding: 0 var(--bf-sp-1);
      border-bottom: 3px solid transparent;
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-sm);
      font-weight: var(--bf-fw-semibold);
      letter-spacing: var(--bf-tr-nudge);
      color: var(--bf-fg-1);
      text-decoration: none;
      white-space: nowrap;
      transition:
        color var(--bf-dur-fast) var(--bf-ease),
        border-color var(--bf-dur-fast) var(--bf-ease);

      &:hover {
        color: var(--bf-orange-dark);
      }

      &:focus-visible {
        outline: 2px solid var(--bf-orange-dark);
        outline-offset: -2px;
      }

      &[aria-current='page'] {
        color: var(--bf-orange-dark);
        border-bottom-color: var(--bf-orange-light);
      }
    }

    /* Takes the leftover width so its child can centre in it, and shrinks before the brand or the
       actions do — the notice is the one thing in the bar that may be clipped. */
    &__center {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1 1 auto;
      min-width: 0;
      margin: 0 var(--bf-sp-2);
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: var(--application-header-action-gap);
      flex: 0 0 auto;
    }

    &__disclosure {
      position: relative;
      display: flex;
    }

    &__trigger,
    &__menu-button {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: var(--bf-sp-1);
      min-width: 44px;
      height: 44px;
      padding: 0 var(--bf-sp-2);
      border: 0;
      border-radius: var(--bf-r-md);
      background: transparent;
      color: var(--bf-slate);
      font: inherit;
      cursor: pointer;
      transition:
        background var(--bf-dur-fast) var(--bf-ease),
        color var(--bf-dur-fast) var(--bf-ease);
    }

    &__trigger:hover,
    &__menu-button:hover,
    &__trigger[aria-expanded='true'] {
      background: var(--bf-surface-hover);
      color: var(--bf-fg-1);
    }

    &__trigger:focus-visible,
    &__menu-button:focus-visible {
      outline: 2px solid var(--bf-orange-dark);
      outline-offset: 2px;
    }

    &__icon {
      width: 20px;
      height: 20px;
      flex: 0 0 auto;
      color: currentColor;
    }

    &__menu-button {
      display: none;
    }

    &__badge {
      position: absolute;
      top: 5px;
      right: 5px;
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--bf-r-pill);
      background: var(--bf-error-accent);
      color: var(--bf-white);
      font-size: var(--bf-fs-2xs);
      font-weight: var(--bf-fw-semibold);
      line-height: 1;
      letter-spacing: var(--bf-tr-nudge);
      box-shadow: 0 0 0 2px var(--bf-white);
    }

    /* Takes the name's place below --bf-bp-sm; the two are never both on. */
    &__avatar {
      display: none;
      flex: 0 0 auto;
      width: 28px;
      height: 28px;
      place-items: center;
      border-radius: var(--bf-r-pill);
      background: linear-gradient(135deg, var(--bf-orange-light), var(--bf-orange-dark));
      color: var(--bf-white);
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-2xs);
      font-weight: var(--bf-fw-bold);
      line-height: 1;
    }

    &__user-name {
      max-width: 180px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: var(--bf-fs-sm);
      font-weight: var(--bf-fw-medium);
      color: var(--bf-slate);
    }

    &__caret {
      width: 16px;
      height: 16px;
      flex: 0 0 auto;
      color: var(--bf-text-3);
      transition: transform var(--bf-dur-base) var(--bf-ease);
    }

    &__trigger[aria-expanded='true'] &__caret {
      transform: rotate(180deg);
    }
  }

  /* One modifier flips the surface to --bf-deep-blue and every foreground to
     white; the logo keeps its orange accent with a white base. Menus that drop
     from the bar stay white — they are surfaces, not part of the bar. */
  .application-header--dark {
    background: var(--bf-deep-blue);
    border-bottom-color: transparent;
    color: var(--bf-white);

    .application-header__navigation-link {
      color: var(--bf-white);

      &:hover,
      &[aria-current='page'] {
        color: var(--bf-orange-light);
      }
    }

    .application-header__trigger,
    .application-header__menu-button,
    .application-header__user-name {
      color: var(--bf-white);
    }

    .application-header__caret {
      color: var(--bf-white);
      opacity: 0.7;
    }

    .application-header__trigger:hover,
    .application-header__menu-button:hover,
    .application-header__trigger[aria-expanded='true'] {
      background: rgba(255, 255, 255, 0.1);
      color: var(--bf-white);
    }

    .application-header__badge {
      box-shadow: 0 0 0 2px var(--bf-deep-blue);
    }

    .application-header__trigger:focus-visible,
    .application-header__menu-button:focus-visible,
    .application-header__logo-link:focus-visible,
    .application-header__navigation-link:focus-visible {
      outline-color: var(--bf-orange-light);
    }
  }

  @media (max-width: 860px) {
    /* --bf-bp-console — the hamburger + drawer own navigation below here */
    .application-header__menu-button {
      display: inline-flex;
    }

    .application-header__navigation {
      display: none;
    }
  }

  /* --bf-bp-sm: the name goes and the avatar takes its place, and the bar is sized for a viewport
     where its controls are the whole content. Viewport queries, not container queries — the header
     is the outermost element of the page and has no container to ask. */
  @media (max-width: 600px) {
    .application-header__user-name {
      display: none;
    }

    .application-header__avatar {
      display: grid;
    }

    /* The bar is sized for the viewport, not just its contents: sticky at 88px costs that height at
       every scroll position on a 700px-tall screen. 60px is the 44px trigger plus 8px of air above
       and below — the hit area is untouched, only the padding it sits in. The logo steps to 32px so
       it still clears the bar's inner edge, the side padding to 16px because the content is now
       three controls that want the width more than the margin, and the action gap drops because
       three 44px targets are already separated by their own boxes: 16px between them costs 32px of
       width on a 360px screen without making anything easier to hit. */
    .application-header {
      height: var(--application-header-height-narrow);
      --application-header-padding-horizontal: 16px;
      --application-header-action-gap: 4px;
    }

    .application-header__logo-link :deep(.application-header__logo),
    .application-header__logo-link :deep(svg) {
      height: 32px;
    }

    /* The hamburger outranks the other two — below this width it is the only way into the
       navigation — and gets its side padding dropped rather than a bigger box, so the 44px hit area
       and the button's width are unchanged and only the glyph grows. */
    .application-header__icon {
      width: 24px;
      height: 24px;
    }

    .application-header__menu-button {
      padding: 0;
    }

    /* The card ships a second symbol (#icon-menu-large) because its glyph comes from a sprite and
       CSS cannot reach into a <use> shadow tree to change the baked stroke-width — at 36px the
       2.0 stroke renders 3px. Ours is a real component, so one declaration does the same job:
       a CSS property beats the SVG presentation attribute. Same geometry, 1.4 stroke. */
    .application-header__menu-button .application-header__icon {
      width: 36px;
      height: 36px;
      stroke-width: 1.4;
    }

    /* The panel is viewport-bounded, not trigger-anchored: the trigger sits within ~60px of the
       right edge, so a 280-360px panel hanging from `right: 0` puts its left edge off-screen and
       the rows' text becomes unreachable. `position: fixed` on the horizontal axis is what "pinned
       to the gutters" means; the vertical anchor is the bar, so the panel opens directly under the
       sticky header.

       100dvh, not 100vh: vh on mobile Safari is the large viewport, so a `100vh - 60px` panel is
       taller than the visible area whenever the URL bar is shown. `overscroll-behavior: contain`
       stops the list's end from chaining into the page scroll.

       Scoped to the header's own panels — BfApplicationMenu is reused by Record row inside page
       content, where a fixed panel would tear away from its row. */
    .application-header :deep(.application-menu) {
      position: fixed;
      top: var(--application-header-height-narrow);
      right: var(--application-header-padding-horizontal);
      left: var(--application-header-padding-horizontal);
      width: auto;
      min-width: 0;
      max-width: none;
      max-height: calc(100dvh - var(--application-header-height-narrow) - var(--bf-sp-3));
      overflow-y: auto;
      overscroll-behavior: contain;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .application-header__caret {
      transition: none;
    }
  }
</style>
